<script setup>
import { ref, computed, onMounted, nextTick, watch } from 'vue';
import Loading from '@/components/common/Loading.vue';
import { useStore } from 'vuex';
import Multiselect from '@suadelabs/vue3-multiselect';
import { $api } from '@/services';
import { useRouter, useRoute } from 'vue-router';
import ListQuestion from './ListQuestion.vue';
import SendToParent from './modal/SendToParent.vue';
import Order from './modal/Order.vue';
import Checkbox from '@/components/elements/checkbox/Checkbox.vue';
import UpdateModal from './modal/UpdateModal.vue';
import DaModalFrame from '@/components/common/daModalFrame/DaModalFrame.vue';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';

const modalIsOpen = ref(false);
const router = useRouter();
const route = useRoute();
const store = useStore();
const sideOverShow = ref(false);
const isLoading = ref(false);
const surveyFormRef = ref(null);
const profileName = computed(() => store.state.profile.staff.name);
const preview = ref(false);
const type = ref('');
const survey = ref({
	name: '',
	description: '',
	subject: '',
	questions: [
		{
			_key: 1,
			title: '',
			question_type: 'multiple_choice',
			content: { options: ['', ''] },
		},
	],
	conclusion: '',
});
let questionKey = 1;
const error = ref('');
const questionType = ref([
	{
		title: 'Trắc nghiệm',
		question_type: 'multiple_choice',
	},
	{
		title: 'Chọn nhiều',
		question_type: 'checkbox',
	},
	{
		title: 'Lưới trắc nghiệm',
		question_type: 'grid',
	},
	{
		title: 'Trả lời',
		question_type: 'text_entry',
	},
]);

onMounted(() => {
	if (route.query.templateId || route.query.id) fetchData();
});

watch(preview, async () => {
	if (!preview.value) {
		await nextTick();
		resizeAllAutoGrowTextareas();
	}
});

const normalizeQuestionDescription = (question) => {
	const hasText = Boolean(question.description?.trim());
	question.showDescription = hasText;
	if (!hasText) {
		delete question.description;
	}
	return question;
};

const OTHER_OPTION_LABEL = 'Khác';

const getLastOption = (options) =>
	options?.length ? options[options.length - 1] : undefined;

const showOtherOptionInput = (question) => {
	const { other_option, options } = question?.content ?? {};
	if (other_option == null || String(other_option).trim() === '') {
		return false;
	}
	return (
		String(other_option).trim() ===
		String(getLastOption(options) ?? '').trim()
	);
};

const hasOtherOption = (question) => {
	const otherOption = question?.content?.other_option;
	return otherOption != null && String(otherOption).trim() !== '';
};

const getEditableOptionEntries = (question) =>
	(question?.content?.options ?? []).flatMap((_, index, options) => {
		if (
			showOtherOptionInput(question) &&
			index === options.length - 1
		) {
			return [];
		}
		return [{ index }];
	});

const fetchData = async () => {
	const res = route.query.templateId
		? await $api.survey.getTemplateDetail(route.query.templateId)
		: await $api.survey.getSurveyDetail(route.query.id);
	survey.value = res.data.data;
	if (route.query.templateId) {
		survey.value.name = '';
		survey.value.id = null;
	}
	survey.value.questions = survey.value.questions.map((question) => {
		const normalized = normalizeQuestionDescription(question);
		const next = {
			...normalized,
			_key: normalized._key ?? normalized.id ?? ++questionKey,
		};

		if (next.question_type === 'checkbox' && next.content) {
			const { other_option, ...content } = next.content;
			if (other_option == null || String(other_option).trim() === '') {
				next.content = content;
			} else {
				const label = String(other_option).trim();
				const options = [...(content.options ?? [])];
				if (String(getLastOption(options) ?? '').trim() !== label) {
					options.push(label);
				}
				next.content = { ...content, options, other_option: label };
			}
		}

		return next;
	});
	await nextTick();
	resizeAllAutoGrowTextareas();
};

const addOption = (question) => {
	if (!Array.isArray(question.content.options)) {
		question.content.options = [];
	}

	if (showOtherOptionInput(question)) {
		question.content.options.splice(question.content.options.length - 1, 0, '');
		return;
	}

	question.content.options.push('');
};

const addDifferentOption = (questionIndex) => {
	const question = survey.value.questions[questionIndex];
	if (
		!question ||
		question.question_type !== 'checkbox' ||
		hasOtherOption(question)
	) {
		return;
	}

	const content = question.content ?? { options: [] };
	const options = [...(content.options ?? []), OTHER_OPTION_LABEL];
	survey.value.questions[questionIndex] = {
		...question,
		content: {
			...content,
			options,
			other_option: OTHER_OPTION_LABEL,
		},
	};
};

const removeOtherOption = (questionIndex) => {
	const question = survey.value.questions[questionIndex];
	if (!question?.content) return;

	const { other_option, ...content } = question.content;
	let options = [...(content.options ?? [])];
	const label = String(other_option ?? '').trim();
	if (
		label &&
		String(getLastOption(options) ?? '').trim() === label
	) {
		options = options.slice(0, -1);
	}
	survey.value.questions[questionIndex] = {
		...question,
		content: {
			...content,
			options,
		},
	};
};

const cloneQuestionContent = (content, questionType) => {
	switch (questionType) {
		case 'grid':
			return {
				rows: [...(content.rows ?? [])],
				columns: [...(content.columns ?? [])],
			};
		case 'multiple_choice':
			return { options: [...(content.options ?? [])] };
		case 'checkbox': {
			const cloned = { options: [...(content.options ?? [])] };
			if (
				content?.other_option != null &&
				String(content.other_option).trim() !== ''
			) {
				cloned.other_option = content.other_option;
			}
			return cloned;
		}
		case 'text_entry':
			return { placeholder: 'Nhập câu trả lời' };
		default:
			return { ...content };
	}
};

const duplicateQuestion = (index) => {
	const question = survey.value.questions[index];
	const duplicated = {
		_key: ++questionKey,
		title: question.title,
		question_type: question.question_type,
		content: cloneQuestionContent(question.content, question.question_type),
	};
	if (question.showDescription) {
		duplicated.showDescription = true;
		duplicated.description = question.description ?? '';
	}
	survey.value.questions.splice(index + 1, 0, duplicated);
};

const sortQuestion = (index) => {
	type.value = 'order';
	sideOverShow.value = true;
};

const deleteQuestion = (index) => {
	survey.value.questions.splice(index, 1);
};
const getQuestionTypeOption = (type) =>
	questionType.value.find((t) => t.question_type === type) ?? null;

const emptyGridValues = () => Array.from({ length: 3 }, () => '');

const getDefaultContent = (type) => {
	switch (type) {
		case 'grid':
			return {
				rows: emptyGridValues(),
				columns: emptyGridValues(),
			};
		case 'multiple_choice':
			return { options: ['', ''] };
		case 'checkbox':
			return { options: ['', ''] };
		case 'text_entry':
			return { placeholder: 'Nhập câu trả lời' };
		case 'section':
			return {};
		default:
			return {};
	}
};

const autoGrowEl = (el) => {
	if (!el) return;
	el.style.height = 'auto';
	el.style.height = `${el.scrollHeight}px`;
};

const autoGrow = (event) => autoGrowEl(event?.target);

const resizeAllAutoGrowTextareas = () => {
	nextTick(() => {
		surveyFormRef.value
			?.querySelectorAll('.auto-grow-textarea')
			.forEach(autoGrowEl);
	});
};

const setQuestionType = (question, option) => {
	const type = option?.question_type ?? 'multiple_choice';
	if (question.question_type === type) return;
	question.question_type = type;
	question.content = getDefaultContent(type);
};

const resolveQuestionType = (questionType) =>
	questionType === 'section' ? 'section' : questionType || 'multiple_choice';

const createEmptyQuestion = (questionType) => {
	const resolvedType = resolveQuestionType(questionType);
	return {
		_key: ++questionKey,
		title: '',
		question_type: resolvedType,
		content: getDefaultContent(resolvedType),
	};
};

const addQuestionAt = (index, questionType) => {
	if (!Array.isArray(survey.value.questions)) {
		survey.value.questions = [];
	}
	const insertIndex = Math.min(
		Math.max(Number(index) || 0, 0),
		survey.value.questions.length
	);
	survey.value.questions.splice(
		insertIndex,
		0,
		createEmptyQuestion(questionType)
	);
};

const deleteOption = (question, optionIndex) => {
	question.content.options.splice(optionIndex, 1);
};

const hasGridValue = (value) => String(value ?? '').trim() !== '';

const sanitizeGridContent = (content) => ({
	rows: (content?.rows ?? []).filter(hasGridValue),
	columns: (content?.columns ?? []).filter(hasGridValue),
});

const sanitizeOptionsContent = (content) => {
	const { other_option, ...rest } = content ?? {};
	const options = (rest.options ?? []).filter(hasGridValue);

	if (other_option != null && hasGridValue(other_option)) {
		const label = String(other_option).trim();
		if (String(getLastOption(options) ?? '').trim() !== label) {
			return { ...rest, options: [...options, label], other_option: label };
		}
		return { ...rest, options, other_option: label };
	}

	return { ...rest, options };
};

const prepareSurveyPayload = () => {
	const payload = JSON.parse(JSON.stringify(survey.value));
	payload.questions = (payload.questions ?? []).map((question) => {
		if (question.question_type === 'grid') {
			return {
				...question,
				content: sanitizeGridContent(question.content),
			};
		}
		if (
			question.question_type === 'multiple_choice' ||
			question.question_type === 'checkbox'
		) {
			return {
				...question,
				content: sanitizeOptionsContent(question.content),
			};
		}
		return question;
	});
	if(route.query.templateId) {
		payload.survey_template_id = route.query.templateId;
	}
	return payload;
};

const exit = async () => {
	try {
		const res = await $api.survey.createDraftSurvey({
			survey: prepareSurveyPayload(),
		});
		if (res) {
			await store.dispatch('setAlertMessage', {
				message: 'Tạo khảo sát thành công',
				type: 'success',
			});
			router.push('/survey');
		}
	} catch (err) {
		if (err?.response?.data?.errors_by_field['name']) {
			error.value = 'Tên phiếu khảo sát không được trùng với tên đã có';
			setTimeout(() => {
				error.value = '';
			}, 3000);
		}
	}
};

const openSendToParentModal = () => {
	type.value = 'send';
	sideOverShow.value = true;
};

const sendToParent = async () => {
	try {
		const res = await $api.survey.sendSurveyToParent(survey.value.id, {
			survey: survey.value,
		});
		console.log(res);
	} catch (err) {
		console.log(err);
	}
};

const addDescription = (index) => {
	const question = survey.value.questions[index];
	question.showDescription = true;
	question.description = question.description ?? '';
};

const deleteDescription = (index) => {
	const question = survey.value.questions[index];
	question.showDescription = false;
	delete question.description;
};

const hasDescription = (question) => question.showDescription === true;

const addRow = (question) => {
	question.content.rows.push('');
};

const addColumn = (question) => {
	question.content.columns.push('');
};

const removeGridRow = (question, rowIndex) => {
	question.content.rows.splice(rowIndex, 1);
};

const removeGridColumn = (question, columnIndex) => {
	if (!Array.isArray(question?.content?.columns)) return;
	question.content.columns.splice(columnIndex, 1);
};

const applyChanges = async () => {
	if (survey.value.status === 'draft') {
		try {
			const res = await $api.survey.updateSurvey(survey.value.id, {
				survey: prepareSurveyPayload(),
			});
			if (res) {
				await store.dispatch('setAlertMessage', {
					message: 'Lưu khảo sát thành công',
					type: 'success',
				});
				router.push('/survey');
			}
		} catch (err) {
			if (err?.response?.data?.errors_by_field['name']) {
				error.value =
					'Tên phiếu khảo sát không được trùng với tên đã có';
				setTimeout(() => {
					error.value = '';
				}, 3000);
			}
		}
	} else {
		exit();
	}
};

const back = () => {
	if (preview.value) {
		preview.value = false;
	} else {
		if (route.query.id) {
			openUpdateModal('exit');
		} else applyChanges();
	}
};

const typeModal = ref('');

const openUpdateModal = (value) => {
	modalIsOpen.value = true;
	typeModal.value = value;
};
</script>
<template>
	<div v-if="sideOverShow" :class="sideOverShow ? 'side-over--active' : ''">
		<div
			class="fixed h-screen w-screen top-0 left-0 bg-black opacity-20 z-30"
			@click="sideOverShow = false"
		></div>
		<SendToParent
			v-if="type === 'send'"
			type="'send'"
			@close="sideOverShow = false"
			@submit="sendToParent"
			:survey="survey"
		/>
		<Order
			v-if="type === 'order'"
			@close="sideOverShow = false"
			:questions="survey.questions"
		/>
	</div>
	<DaModalFrame v-model:open="modalIsOpen">
		<UpdateModal
			@close="modalIsOpen = false"
			:id="survey.id"
			:type="typeModal"
			@submit="applyChanges"
			:status="survey.status"
		/>
	</DaModalFrame>
	<div class="px-12 md-down:px-4 p-10">
		<div v-if="isLoading" class="pt-20">
			<Loading />
		</div>
		<div v-else ref="surveyFormRef">
			<div
				class="flex items-center gap-2 text-sm text-neutral-500 font-semibold mb-4"
			>
				<div @click="router.push('/survey')" class="cursor-pointer">
					Phiếu khảo sát
				</div>
				<img alt="" src="@/assets/images/icons/arrow-right.png" />
				<div v-if="preview">Xem trước</div>
				<div v-else-if="survey.status === 'draft'">Chỉnh sửa</div>
				<div v-else>Tạo mới</div>
			</div>
			<div
				class="flex flex-nowrap justify-between items-center mb-10 gap-4"
			>
				<div class="min-w-0 flex-1">
					<div
						class="text-neutral-700 font-bold flex items-center text-4xl gap-3 min-w-0"
					>
						<img
							src="@/assets/images/icons/arrow-right.png"
							alt="arrow-right"
							class="w-5 h-5 shrink-0 cursor-pointer"
							style="transform: rotate(180deg)"
							@click="back"
						/>
						<img
							src="@/assets/images/icons/form-evaluation.png"
							class="w-7 h-7 shrink-0"
						/>
						<div
							class="min-w-0 break-words whitespace-pre-wrap flex-1"
						>
							{{
								survey.name ? survey.name : 'Tạo phiếu khảo sát'
							}}
						</div>
					</div>
					<div class="text-green-500 text-xs font-semibold">
						TẠO BỞI
						<span class="text-blue-600 text-sm">{{
							profileName
						}}</span>
						LÚC
						{{ $filters.formatDateTimeNotTz(new Date(Date.now())) }}
						<span v-if="survey.status === 'draft'">| BẢN NHÁP</span>
					</div>
				</div>
				<div
					v-if="
						survey.name &&
						survey.questions.some((question) => question.title)
					"
					class="flex items-center gap-2"
				>
					<button
						v-if="preview"
						class="button-p btn--ghost w-fit px-4"
						type="button"
						@click="preview = false"
					>
						Tiếp tục chỉnh sửa
					</button>
					<button
						v-else
						class="button-p btn--ghost w-fit px-4"
						type="button"
						@click="preview = true"
					>
						Xem trước
					</button>
					<button
						v-if="!preview"
						class="button-p w-fit px-4"
						type="button"
						@click="applyChanges"
					>
						Lưu thay đổi
					</button>
					<button
						v-if="preview"
						class="button-p w-fit px-4"
						type="button"
						@click="openSendToParentModal"
					>
						Gửi phụ huynh
					</button>
					<Menu as="div" class="relative">
						<div>
							<MenuButton
								class="rounded-full flex text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
							>
								<span class="sr-only">Open user menu</span>
								<svg
									fill="none"
									height="20"
									viewBox="0 0 20 20"
									width="20"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										clip-rule="evenodd"
										d="M11.875 16.25C11.875 17.2855 11.0355 18.125 10 18.125C8.96447 18.125 8.125 17.2855 8.125 16.25C8.125 15.2145 8.96447 14.375 10 14.375C11.0355 14.375 11.875 15.2145 11.875 16.25ZM11.875 10C11.875 11.0355 11.0355 11.875 10 11.875C8.96447 11.875 8.125 11.0355 8.125 10C8.125 8.96447 8.96447 8.125 10 8.125C11.0355 8.125 11.875 8.96447 11.875 10ZM11.875 3.75C11.875 4.78553 11.0355 5.625 10 5.625C8.96447 5.625 8.125 4.78553 8.125 3.75C8.125 2.71447 8.96447 1.875 10 1.875C11.0355 1.875 11.875 2.71447 11.875 3.75Z"
										fill="#94A3B8"
										fill-rule="evenodd"
									/>
								</svg>
							</MenuButton>
						</div>
						<transition
							enter-active-class="transition ease-out duration-200"
							enter-from-class="transform opacity-0 scale-95"
							enter-to-class="transform opacity-100 scale-100"
							leave-active-class="transition ease-in duration-75"
							leave-from-class="transform opacity-100 scale-100"
							leave-to-class="transform opacity-0 scale-95"
						>
							<MenuItems
								class="origin-top-right absolute right-0 mt-2 w-60 rounded-md py-1 bg-white focus:outline-none z-10 shadow-sm border border-yellow-100"
							>
								<MenuItem
									v-if="survey.status !== 'draft'"
									v-slot="{ active }"
									@click="openSendToParentModal"
								>
									<div
										:class="active ? '' : ''"
										class="p-2 px-4 bg-white z-10 text-gray-600 hover:bg-neutral-50 flex items-center gap-4 cursor-pointer"
									>
										<img
											src="@/assets/images/icons/send.png"
											alt=""
											class="w-4 h-4"
										/>
										<span>Gửi phụ huynh</span>
									</div>
								</MenuItem>
								<MenuItem
									v-if="survey.status !== 'draft'"
									v-slot="{ active }"
									@click="openUpdateModal('exit')"
								>
									<div
										:class="active ? '' : ''"
										class="p-2 px-4 bg-white z-10 text-gray-600 hover:bg-neutral-50 flex items-center gap-4 cursor-pointer"
									>
										<img
											src="@/assets/images/icons/exit.png"
											alt=""
											class="w-4 h-4"
										/>
										<span>Thoát</span>
									</div>
								</MenuItem>
								<MenuItem
									v-if="survey.status === 'draft'"
									v-slot="{ active }"
									@click="openUpdateModal('deleteSurvey')"
								>
									<div
										:class="active ? '' : ''"
										class="p-2 px-4 bg-white z-10 text-gray-600 hover:bg-neutral-50 flex items-center gap-4 cursor-pointer"
									>
										<img
											src="@/assets/images/icons/trash.png"
											alt=""
											class="w-4 h-4"
										/>
										<span>Xoá</span>
									</div>
								</MenuItem>
							</MenuItems>
						</transition>
					</Menu>
				</div>
			</div>
			<div class="max-w-3xl mx-auto">
				<div v-if="preview">
					<ListQuestion :survey="survey" />
				</div>
				<div v-else>
					<div class="relative mb-10">
						<div
							class="absolute inset-0 flex items-center"
							aria-hidden="true"
						>
							<div class="w-full border-t border-neutral-300" />
						</div>
						<div class="relative flex justify-center">
							<div
								class="bg-body px-2 text-neutral-600 font-semibold whitespace-nowrap items-center text-lg"
							>
								Tên phiếu khảo sát
							</div>
						</div>
					</div>
					<div class="bg-white rounded-lg p-6 shadow-sm mb-6">
						<div class="input-group mb-4 relative">
							<div
								class="text-neutral-600 font-semibold text-sm whitespace-nowrap mb-1"
							>
								Tên phiếu khảo sát
							</div>
							<textarea
								v-model="survey.name"
								placeholder="Tên phiếu khảo sát"
								class="auto-grow-textarea"
								rows="1"
								@input="autoGrow"
								:class="error ? 'text-error' : ''"
							/>
							<div
								v-if="error"
								class="text-sienna-600 text-sm absolute -bottom-4 left-0"
							>
								{{ error }}
							</div>
						</div>
						<div class="input-group">
							<div
								class="text-neutral-600 font-semibold text-sm whitespace-nowrap mb-1"
							>
								Lời mở đầu
							</div>
							<textarea
								v-model="survey.description"
								placeholder="Nhập lời mở đầu"
								class="auto-grow-textarea"
								rows="1"
								@input="autoGrow"
							/>
						</div>
					</div>
					<div class="relative py-4">
						<div
							class="absolute inset-0 flex items-center"
							aria-hidden="true"
						>
							<div class="w-full border-t border-neutral-300" />
						</div>
						<div class="relative flex justify-center">
							<div
								class="bg-body px-2 text-neutral-600 font-semibold whitespace-nowrap items-center text-lg"
							>
								Câu hỏi
							</div>
						</div>
					</div>
					<div class="flex flex-col">
						<template
							v-for="(question, index) in survey.questions"
							:key="question._key ?? question.id ?? index"
						>
							<div class="question-insert group gap-4">
								<button
									type="button"
									class="question-insert-btn flex items-center gap-2 text-yellow-700 font-semibold text-sm"
									@click.stop="
										addQuestionAt(index, 'section')
									"
								>
									<img
										src="@/assets/images/icons/yellow-plus.png"
										alt="Add"
										class="w-4 h-4"
									/>
									Thêm tiêu đề
								</button>
								<button
									type="button"
									class="question-insert-btn flex items-center gap-2 text-yellow-700 font-semibold text-sm"
									@click.stop="addQuestionAt(index)"
								>
									<img
										src="@/assets/images/icons/yellow-plus.png"
										alt="Add"
										class="w-4 h-4"
									/>
									Thêm câu hỏi
								</button>
							</div>
							<div class="bg-white rounded-lg shadow-sm mb-4">
								<div class="p-6">
									<div class="mb-4">
										<div
											class="grid grid-cols-10 gap-4 md-down:grid-cols-1"
										>
											<div
												class="md-down:col-span-10"
												:class="
													question.question_type ===
													'section'
														? 'col-span-10'
														: 'col-span-7'
												"
											>
												<div
													class="text-neutral-600 font-semibold text-sm whitespace-nowrap mb-1"
												>
													{{
														question.question_type !==
														'section'
															? 'Câu hỏi'
															: 'Tiêu đề'
													}}
												</div>
												<div
													class="flex items-center gap-2"
												>
													<textarea
														v-model="question.title"
														:placeholder="
															question.question_type !==
															'section'
																? 'Nhập câu hỏi'
																: 'Nhập tiêu đề'
														"
														class="auto-grow-textarea"
														rows="1"
														@input="autoGrow"
													/>
													<img
														v-if="
															question.question_type ===
															'section'
														"
														src="@/assets/images/icons/trash-fill-blue.png"
														alt="trash"
														class="w-6 h-6 cursor-pointer"
														@click="
															deleteQuestion(
																index
															)
														"
													/>
												</div>
											</div>
											<div
												class="col-span-3 md-down:col-span-10"
												v-if="
													question.question_type !==
													'section'
												"
											>
												<div
													class="text-neutral-600 font-semibold text-sm whitespace-nowrap mb-1"
												>
													Loại câu hỏi
												</div>

												<Multiselect
													class="question-type-select"
													:model-value="
														getQuestionTypeOption(
															question.question_type
														)
													"
													@update:model-value="
														setQuestionType(
															question,
															$event
														)
													"
													:options="questionType"
													track-by="question_type"
													label="title"
													placeholder="Chọn loại câu hỏi"
													selectLabel=""
													deselectLabel=""
													selectedLabel=""
												>
													<template
														#option="{ option }"
													>
														<div
															class="flex items-center gap-2"
														>
															<img
																v-if="
																	option.question_type ===
																	'multiple_choice'
																"
																src="@/assets/images/icons/multiple_choice.png"
																alt="Trắc nghiệm"
																class="w-4 h-4 shrink-0"
															/>
															<img
																v-else-if="
																	option.question_type ===
																	'checkbox'
																"
																src="@/assets/images/icons/checkbox.png"
																alt="Chọn nhiều"
																class="w-4 h-4 shrink-0"
															/>
															<img
																v-else-if="
																	option.question_type ===
																	'grid'
																"
																src="@/assets/images/icons/grid.png"
																alt="Lưới trắc nghiệm"
																class="w-4 h-4 shrink-0"
															/>
															<img
																v-else-if="
																	option.question_type ===
																	'text_entry'
																"
																src="@/assets/images/icons/text_entry.png"
																alt="Trả lời"
																class="w-4 h-4 shrink-0"
															/>
															<div
																class="text-neutral-600 text-sm font-normal"
															>
																{{
																	option.title
																}}
															</div>
														</div>
													</template>
													<template
														#singleLabel="{
															option,
														}"
													>
														<div
															class="flex items-center gap-2"
														>
															<img
																v-if="
																	option.question_type ===
																	'multiple_choice'
																"
																src="@/assets/images/icons/multiple_choice.png"
																alt="Trắc nghiệm"
																class="w-4 h-4 shrink-0"
															/>
															<img
																v-else-if="
																	option.question_type ===
																	'checkbox'
																"
																src="@/assets/images/icons/checkbox.png"
																alt="Chọn nhiều"
																class="w-4 h-4 shrink-0"
															/>
															<img
																v-else-if="
																	option.question_type ===
																	'grid'
																"
																src="@/assets/images/icons/grid.png"
																alt="Lưới trắc nghiệm"
																class="w-4 h-4 shrink-0"
															/>
															<img
																v-else-if="
																	option.question_type ===
																	'text_entry'
																"
																src="@/assets/images/icons/text_entry.png"
																alt="Trả lời"
																class="w-4 h-4 shrink-0"
															/>
															<div
																class="text-neutral-600 text-sm font-normal"
															>
																{{
																	option.title
																}}
															</div>
														</div>
													</template>
												</Multiselect>
											</div>
										</div>
									</div>
									<div
										v-if="
											question.question_type !== 'section'
										"
									>
										<button
											v-if="!hasDescription(question)"
											type="button"
											class="flex items-center gap-2 text-yellow-700 font-semibold text-sm mb-4"
											@click.stop="addDescription(index)"
										>
											<img
												src="@/assets/images/icons/yellow-plus.png"
												alt="Add"
												class="w-4 h-4"
											/>
											Thêm mô tả
										</button>
										<div
											v-else
											class="flex items-center gap-2 mb-4"
										>
											<textarea
												v-model="question.description"
												placeholder="Nhập mô tả"
												rows="1"
												class="auto-grow-textarea flex-1"
												@input="autoGrow"
											></textarea>
											<img
												src="@/assets/images/icons/trash-fill-blue.png"
												alt="trash"
												class="w-6 h-6 cursor-pointer shrink-0 mt-1"
												@click="
													deleteDescription(index)
												"
											/>
										</div>
									</div>
									<div
										v-if="
											question.question_type ===
												'multiple_choice' ||
											question.question_type ===
												'checkbox'
										"
									>
										<div class="flex flex-col gap-4">
											<div
												v-for="{
													index: indexOption,
												} in getEditableOptionEntries(
													question
												)"
												:key="indexOption"
											>
												<div>
													<div
														class="flex items-center gap-2"
													>
														<input
															v-if="
																question.question_type ===
																'multiple_choice'
															"
															type="radio"
															disabled
															class="w-4 h-4 text-yellow-600 border-neutral-400 border-2 focus:ring-yellow-600"
														/>
														<Checkbox
															v-if="
																question.question_type ===
																'checkbox'
															"
															:disabledAction="
																true
															"
														/>
														<textarea
															type="text"
															v-model="
																question.content
																	.options[
																	indexOption
																]
															"
															:placeholder="`Nhập tuỳ chọn ${
																indexOption + 1
															}`"
															rows="1"
															@input="autoGrow"
															class="auto-grow-textarea"
														/>
														<img
															src="@/assets/images/icons/trash-fill-blue.png"
															alt="trash"
															class="w-6 h-6 cursor-pointer"
															v-if="
																question.content
																	.options
																	.length > 2
															"
															@click="
																deleteOption(
																	question,
																	indexOption
																)
															"
														/>
													</div>
												</div>
											</div>
											<div
												v-if="
													question.question_type ===
														'checkbox' &&
													showOtherOptionInput(
														question
													)
												"
											>
												<div
													class="text-neutral-600 font-semibold text-sm mb-1"
												>
													{{
														question.content
															.other_option
													}}
												</div>
												<div
													class="flex items-center gap-2"
												>
													<textarea
														placeholder="Phụ huynh nhập nội dung"
														rows="1"
														disabled
														readonly
														class="auto-grow-textarea w-full bg-gray-50"
													/>
													<img
														src="@/assets/images/icons/trash-fill-blue.png"
														alt="trash"
														class="w-6 h-6 cursor-pointer"
														@click="
															removeOtherOption(
																index
															)
														"
													/>
												</div>
											</div>
										</div>
										<div
											class="flex items-center gap-8 mt-4"
										>
											<div
												class="flex items-center gap-2 cursor-pointer"
												@click="addOption(question)"
											>
												<img
													src="@/assets/images/icons/yellow-plus.png"
													alt="Add"
													class="w-4 h-4"
												/>
												<div
													class="text-yellow-700 text-sm font-semibold"
												>
													Thêm tuỳ chọn
												</div>
											</div>
											<button
												type="button"
												class="flex items-center gap-2 cursor-pointer"
												@click.stop="
													addDifferentOption(index)
												"
												v-if="
													question.question_type ===
														'checkbox' &&
													!hasOtherOption(question)
												"
											>
												<img
													src="@/assets/images/icons/yellow-plus.png"
													alt="Add"
													class="w-4 h-4"
												/>
												<div
													class="text-yellow-700 text-sm font-semibold"
												>
													Thêm tuỳ chọn khác
												</div>
											</button>
										</div>
									</div>
									<div
										v-if="question.question_type === 'grid'"
									>
										<div class="input-group">
											<div class="grid grid-cols-2 gap-4">
												<div>
													<div
														class="text-neutral-600 font-semibold text-sm whitespace-nowrap"
													>
														Hàng
													</div>
													<div
														class="flex flex-col gap-1"
													>
														<div
															v-for="(
																row, indexRow
															) in question
																.content.rows"
															:key="indexRow"
														>
															<div
																class="flex items-center gap-2"
															>
																<div
																	class="text-neutral-700 text-sm"
																>
																	{{
																		indexRow +
																		1
																	}}.
																</div>
																<div
																	class="input-group w-full"
																>
																	<textarea
																		type="text"
																		v-model="
																			question
																				.content
																				.rows[
																				indexRow
																			]
																		"
																		rows="1"
																		placeholder="Nhập giá trị"
																		class="title input-with-icon w-full auto-grow-textarea"
																		@input="
																			autoGrow
																		"
																	/>
																	<div
																		class="input-icon absolute top-1/2"
																	>
																		<img
																			v-if="
																				question
																					.content
																					.rows
																					.length >
																				1
																			"
																			src="@/assets/images/icons/trash-fill-blue.png"
																			alt="trash"
																			class="w-4 h-4 cursor-pointer"
																			@click.stop="
																				removeGridRow(
																					question,
																					indexRow
																				)
																			"
																		/>
																	</div>
																</div>
															</div>
														</div>
													</div>
													<button
														type="button"
														class="flex items-center gap-2 text-yellow-700 font-semibold text-sm mt-4"
														@click.stop="
															addRow(question)
														"
													>
														<img
															src="@/assets/images/icons/yellow-plus.png"
															alt="Add"
															class="w-4 h-4"
														/>
														Thêm hàng
													</button>
												</div>
												<div>
													<div
														class="text-neutral-600 font-semibold text-sm whitespace-nowrap"
													>
														Cột
													</div>
													<div
														class="flex flex-col gap-1"
													>
														<div
															v-for="(
																col, indexColumn
															) in question
																.content
																.columns"
															:key="indexColumn"
														>
															<div
																class="flex items-center gap-2"
															>
																<div
																	class="text-neutral-700 text-sm"
																>
																	{{
																		indexColumn +
																		1
																	}}.
																</div>
																<div
																	class="w-full"
																>
																	<textarea
																		type="text"
																		v-model="
																			question
																				.content
																				.columns[
																				indexColumn
																			]
																		"
																		rows="1"
																		placeholder="Nhập giá trị"
																		class="title input-with-icon w-full auto-grow-textarea"
																		@input="
																			autoGrow
																		"
																	/>
																	<div
																		class="input-icon absolute top-1/2"
																	>
																		<img
																			v-if="
																				question
																					.content
																					.columns
																					.length >
																				1
																			"
																			src="@/assets/images/icons/trash-fill-blue.png"
																			alt="trash"
																			class="w-4 h-4 cursor-pointer"
																			@click.stop="
																				removeGridColumn(
																					question,
																					indexColumn
																				)
																			"
																		/>
																	</div>
																</div>
															</div>
														</div>
													</div>
													<button
														type="button"
														class="flex items-center gap-2 text-yellow-700 font-semibold text-sm mt-4"
														@click.stop="
															addColumn(question)
														"
													>
														<img
															src="@/assets/images/icons/yellow-plus.png"
															alt="Add"
															class="w-4 h-4"
														/>
														Thêm cột
													</button>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div
									class="divider"
									v-if="question.question_type !== 'section'"
								></div>
								<div
									v-if="question.question_type !== 'section'"
									class="p-6 flex items-center justify-end gap-4"
								>
									<div
										class="flex items-center gap-1 cursor-pointer"
										@click="duplicateQuestion(index)"
									>
										<img
											src="@/assets/images/icons/duplicate-fill.png"
											alt="duplicate"
											class="w-4 h-4"
										/>
										<div
											class="text-blue-600 text-sm font-semibold"
										>
											Tạo bản sao
										</div>
									</div>
									<div
										class="flex items-center gap-1 cursor-pointer"
										@click="sortQuestion(index)"
									>
										<img
											src="@/assets/images/icons/order-fill.png"
											alt="order"
											class="w-4 h-4"
										/>
										<div
											class="text-blue-600 text-sm font-semibold"
										>
											Sắp xếp
										</div>
									</div>
									<div
										class="flex items-center gap-1 cursor-pointer"
										@click="deleteQuestion(index)"
									>
										<img
											src="@/assets/images/icons/trash-fill-blue.png"
											alt="trash"
											class="w-4 h-4"
										/>
										<div
											class="text-blue-600 text-sm font-semibold"
										>
											Xoá
										</div>
									</div>
								</div>
							</div>
						</template>
						<div class="question-insert group gap-4">
							<button
								type="button"
								class="question-insert-btn flex items-center gap-2 text-yellow-700 font-semibold text-sm"
								@click.stop="
									addQuestionAt(
										survey.questions.length,
										'section'
									)
								"
							>
								<img
									src="@/assets/images/icons/yellow-plus.png"
									alt="Add"
									class="w-4 h-4"
								/>
								Thêm tiêu đề
							</button>
							<button
								type="button"
								class="question-insert-btn flex items-center gap-2 text-yellow-700 font-semibold text-sm"
								@click.stop="
									addQuestionAt(survey.questions.length)
								"
							>
								<img
									src="@/assets/images/icons/yellow-plus.png"
									alt="Add"
									class="w-4 h-4"
								/>
								Thêm câu hỏi
							</button>
						</div>
					</div>
					<div class="relative py-4">
						<div
							class="absolute inset-0 flex items-center"
							aria-hidden="true"
						>
							<div class="w-full border-t border-neutral-300" />
						</div>
						<div class="relative flex justify-center">
							<div
								class="bg-body px-2 text-neutral-600 font-semibold whitespace-nowrap items-center text-lg"
							>
								Lời kết
							</div>
						</div>
					</div>
					<div class="bg-white rounded-lg p-6 shadow-sm mt-6">
						<div class="input-group mb-4">
							<div
								class="text-neutral-600 font-semibold text-sm whitespace-nowrap mb-1"
							>
								Lời kết
							</div>
							<textarea
								v-model="survey.conclusion"
								placeholder="Nhập lời kết"
								class="auto-grow-textarea"
								rows="1"
								@input="autoGrow"
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
<style scoped>
.divider {
	border-top: 1px solid #e1e7ef;
}

.auto-grow-textarea {
	overflow: hidden;
	resize: none;
}

/* vue3-multiselect (local override) */
:deep(.question-type-select .multiselect__tags) {
	min-height: 42px;
	height: 42px;
	padding: 0 40px 0 12px;
	display: flex;
	align-items: center;
}

:deep(.question-type-select .multiselect__single) {
	position: relative;
	top: auto;
	margin: 0;
	line-height: 1.25;
	display: flex;
	align-items: center;
	height: 100%;
	font-weight: 400;
}

:deep(.question-type-select .multiselect__tags-wrap) {
	display: flex;
	align-items: center;
	height: 100%;
}

:deep(.question-type-select .multiselect__placeholder) {
	margin: 0;
	display: flex;
	align-items: center;
	height: 100%;
}

:deep(.question-type-select .multiselect__select) {
	height: 42px;
}

:deep(.question-type-select .multiselect__option--selected) {
	font-weight: 400 !important;
}

:deep(.question-type-select .multiselect__option--selected::after) {
	display: none;
}

:deep(.question-type-select .multiselect__option--highlight) {
	background: #f5f5f5 !important;
	color: #48566a !important;
	font-weight: 400 !important;
}

:deep(.question-type-select .multiselect__option--highlight::after) {
	background: #f5f5f5 !important;
}

:deep(
		.question-type-select
			.multiselect__option--selected.multiselect__option--highlight
	) {
	background: #f5f5f5 !important;
	color: #48566a !important;
	font-weight: 400 !important;
}

.question-insert {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	height: 1.5rem;
	margin: 0 0 16px 0;
	cursor: pointer;
}

.question-insert-btn {
	position: relative;
	z-index: 1;
	opacity: 0;
	pointer-events: none;
	transition: opacity 0.15s ease;
}

.question-insert:hover .question-insert-btn,
.question-insert:focus-within .question-insert-btn {
	opacity: 1;
	pointer-events: auto;
}
</style>